import { useNavigate, useParams } from 'react-router-dom';
import { SectionDetailSheet } from '@/components/cleaner/SectionDetailSheet';
import { Loader2 } from 'lucide-react';
import { useRole } from '@/contexts/RoleContext';
import { useCleanerSectionShell } from '@/hooks/useCleanerSectionShell';
import { areCleanerShellQueryParamsReady } from '@/lib/cleanerQueryGuards';

export const SectionDetailsPage = () => {
  const navigate = useNavigate();
  const { propertyId, sectionId } = useParams<{ propertyId: string; sectionId: string }>();
  const { currentOrgId } = useRole();

  const {
    data: shellData,
    isPending,
    isError,
    error: queryError,
    refetch,
  } = useCleanerSectionShell(propertyId, sectionId, currentOrgId);

  const shellMatchesUrl =
    shellData &&
    propertyId &&
    sectionId &&
    shellData.section.id === sectionId
      ? shellData
      : null;

  const section = shellMatchesUrl?.section ?? null;
  const propertyAddress = shellMatchesUrl?.propertyAddress ?? '';
  const taskIds = shellMatchesUrl?.taskIds ?? [];
  const isWorkStarted = shellMatchesUrl?.isWorkStarted ?? false;

  const queryEnabled = areCleanerShellQueryParamsReady(propertyId, sectionId, currentOrgId);
  const showFullPageLoader = queryEnabled && isPending && !section;

  if (!queryEnabled) {
    return null;
  }

  if (showFullPageLoader) {
    return (
      <div className="min-h-screen bg-canvas flex items-center justify-center p-6">
        <div className="flex flex-col items-center gap-3 text-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          <p className="text-sm text-muted-foreground">Odzyskiwanie danych...</p>
        </div>
      </div>
    );
  }

  if (isError && !section) {
    const message =
      queryError instanceof Error ? queryError.message : 'Nie udało się załadować danych sekcji.';
    return (
      <div className="min-h-screen bg-canvas flex items-center justify-center p-6">
        <div className="text-center space-y-3">
          <p className="text-sm text-muted-foreground">{message}</p>
          <div className="flex gap-2 justify-center">
            <button
              type="button"
              className="px-4 py-2 rounded-md bg-primary text-primary-foreground"
              onClick={() => void refetch()}
            >
              Spróbuj ponownie
            </button>
            <button
              type="button"
              className="px-4 py-2 rounded-md border border-border"
              onClick={() => navigate(`/property/${propertyId}`)}
            >
              Wróć do budynku
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!section) {
    return (
      <div className="min-h-screen bg-canvas flex items-center justify-center p-6">
        <div className="text-center space-y-3">
          <p className="text-sm text-muted-foreground">Brak danych sekcji.</p>
          <button
            type="button"
            className="px-4 py-2 rounded-md bg-primary text-primary-foreground"
            onClick={() => navigate(`/property/${propertyId}`)}
          >
            Wróć do budynku
          </button>
        </div>
      </div>
    );
  }

  return (
    <SectionDetailSheet
      section={section}
      propertyAddress={propertyAddress}
      locationId={propertyId}
      taskIds={taskIds.length > 0 ? taskIds : undefined}
      taskId={taskIds.length > 0 ? taskIds[0] : propertyId}
      orgId={currentOrgId}
      isWorkStarted={isWorkStarted}
      embedded
      embeddedClassName="animate-in slide-in-from-right duration-300 fade-in fill-mode-both min-h-screen bg-canvas flex flex-col w-full absolute top-0 left-0 z-50"
      onBack={() => navigate(`/property/${propertyId}`)}
    />
  );
};
